import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getComicById, getRelatedComics } from '@/mocks/comics';

export default function ComicPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-emotion-sad-line text-foreground-300 text-3xl"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-800 font-heading">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mt-1">您要查找的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-block mt-4 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = getRelatedComics(comic, 4);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <Breadcrumb />

        {/* Comic Detail Hero */}
        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Cover */}
              <div className="flex-shrink-0 w-48 md:w-56 mx-auto md:mx-0">
                <div className="relative overflow-hidden rounded-xl bg-background-50 aspect-[3/4]">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩国漫画在线阅读 - 歪歪漫画网`}
                    className="w-full h-full object-cover object-top"
                  />
                  <span
                    className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium ${
                      comic.status === '连载中'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-secondary-500 text-background-50'
                    }`}
                  >
                    {comic.status}
                  </span>
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  {comic.title}
                </h1>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <span className="text-sm text-foreground-500">作者：<strong className="text-foreground-700">{comic.author}</strong></span>
                  <span className="text-foreground-300">|</span>
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="text-sm text-primary-500 hover:text-primary-600 font-medium"
                  >
                    {comic.category}
                  </Link>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-500">
                    更新于 {comic.updatedAt}
                  </span>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6 mt-4 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="text-lg font-bold text-foreground-900">{comic.rating}</span>
                    <span className="text-xs text-foreground-400">/ 10</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                    <i className="ri-eye-line text-foreground-400"></i>
                    <span>{comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 次阅读</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                    <i className="ri-heart-line text-foreground-400"></i>
                    <span>{comic.favorites >= 10000 ? `${(comic.favorites / 10000).toFixed(1)}万` : comic.favorites} 收藏</span>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-background-50 text-xs text-foreground-600 border border-background-200/70"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Description */}
                <p className="mt-5 text-sm text-foreground-600 leading-relaxed max-w-2xl">
                  {comic.description}
                </p>

                {/* Actions */}
                <div className="flex items-center gap-3 mt-6 flex-wrap">
                  <a
                    href={`#chapters`}
                    className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    开始阅读
                  </a>
                  <button
                    className="px-6 py-2.5 rounded-full bg-background-50 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-colors border border-background-200/70 whitespace-nowrap"
                  >
                    <i className="ri-heart-line mr-1.5"></i>收藏
                  </button>
                  <button
                    className="px-6 py-2.5 rounded-full bg-background-50 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-colors border border-background-200/70 whitespace-nowrap"
                  >
                    <i className="ri-share-forward-line mr-1.5"></i>分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapters */}
        <section id="chapters" className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                章节列表
                <span className="text-sm font-normal text-foreground-500 ml-2">
                  共 {comic.chapters.length} 话
                </span>
              </h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 rounded-full text-xs font-medium bg-foreground-900 text-background-50 whitespace-nowrap">
                  正序
                </button>
                <button className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap">
                  倒序
                </button>
              </div>
            </div>

            <div className="bg-background-100 rounded-xl p-4 md:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {comic.chapters.map((chapter) => (
                  <a
                    key={chapter.id}
                    href="#"
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-background-50 transition-colors group"
                    onClick={(e) => e.preventDefault()}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                        <span className="text-xs font-medium text-primary-700">{chapter.number}</span>
                      </div>
                      <span className="text-sm text-foreground-700 truncate group-hover:text-primary-500 transition-colors">
                        {chapter.title}
                      </span>
                    </div>
                    <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{chapter.updatedAt}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="w-full py-10 md:py-14 bg-background-100">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} comic={rc} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}